import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Font,
  StyleSheet,
  Image
} from '@react-pdf/renderer';
import moment from 'moment/moment';

import InterRegular from '../../../assets/fonts/Inter-Regular.ttf';
import InterBold from '../../../assets/fonts/Inter-Bold.ttf';

const styles = StyleSheet.create({
  container: {
    padding: 36,
    fontFamily: 'Inter',
    fontSize: 10,
    fontWeight: 'normal',
    color: '#202124',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  titleBlock: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  bold: {
    fontFamily: 'Inter',
    fontWeight: 'bold'
  },
  logo: {
    height: 30,
    width: 'auto',
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    height: 30,
    marginBottom: 4,
  },
  title: {
    fontSize: 18,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    textAlign: 'right',
  },
  row: {
    marginTop: 2,
    flexDirection: 'row',
    justifyContent: 'flex-end'
  },
  table: {
    marginTop: 4,
    paddingBottom: 8,
    borderBottom: '1px solid #000000',
  },
  tableHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontWeight: 'bold',
    fontFamily: 'Inter',
    paddingTop: 4,
    paddingBottom: 4,
    borderTop: '1px solid #000000',
    borderBottom: '1px solid #000000',
    fontSize: 9
  },
  tableHeaderCell: {
    display: 'flex',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontFamily: 'Inter',
  },
  tableRow: {
    marginTop: 8,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  tableCell: {
    fontSize: 8,
    fontFamily: 'Inter'
  },
  borderCell: {
    border: '0.5px solid #969696',
    borderRadius: '4px',
    height: '13px',
    width: '13px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  footer: {
    position: 'fixed',
    bottom: 8,
    left: 0,
    paddingBottom: 2,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottom: '1px solid #000000',
  }
});

Font.register({
  family: 'Inter',
  fonts: [
    {
      src: InterRegular,
    },
    {
      src: InterBold,
      fontWeight: 'bold',
    },
  ],
});

const PickingListPDF = (props) => {
  const {
    activeCase,
    tenant,
    surgeon,
    hospital,
    kits = [],
    items = [],
    user,
  } = props;

  return (
    <Document>
      <Page size="A4" style={styles.container}>
        <View>
          <View style={styles.titleBlock}>
            <View style={{ flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
              <View>
                <Image
                  src={tenant?.documentLogo?.downloadUrl}
                  style={styles.logo}
                />
              </View>
              <View>
                <View style={{ display: 'flex', flexDirection: 'row', marginTop: 8 }}>
                  <View style={{ ...styles.bold, width: 80 }}>
                    <Text>SURGERY</Text>
                  </View>
                  <Text>{moment(activeCase?.date)?.local()?.format('Do MMMM YYYY')}</Text>
                </View>
                <View style={{ display: 'flex', flexDirection: 'row', marginTop: 2 }}>
                  <View style={{ ...styles.bold, width: 80 }}>
                    <Text>SURGEON</Text>
                  </View>
                  <Text>{surgeon ? `${surgeon.title} ${surgeon.firstName} ${surgeon.lastName}` : ''}</Text>
                </View>
                <View style={{ display: 'flex', flexDirection: 'row', marginTop: 2 }}>
                  <View style={{ ...styles.bold, width: 80 }}>
                    <Text>HOSPITAL</Text>
                  </View>
                  <Text>{hospital ? hospital.name : ''}</Text>
                </View>
                <View style={{ display: 'flex', flexDirection: 'row', marginTop: 2 }}>
                  <View style={{ ...styles.bold, width: 80 }}>
                    <Text>PATIENT ID</Text>
                  </View>
                  <Text>{activeCase?.patientReference || ''}</Text>
                </View>
              </View>
            </View>
            <View>
              <View style={styles.titleContainer}>
                <Text style={styles.title}>
                  PICK LIST
                </Text>
              </View>
              <View style={styles.row}>
                <Text>Date: </Text><Text style={styles.bold}>{moment()?.local()?.format('Do MMMM YYYY')}</Text>
              </View>
              <View style={styles.row}>
                <Text>Case ID: </Text><Text style={styles.bold}>{activeCase?.id}</Text>
              </View>
            </View>
          </View>

          <View style={{ marginTop: 24 }}>
            <Text style={{ fontWeight: 'bold' }}>KITS</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <View style={{ ...styles.tableHeaderCell, width: '15%' }}><Text>KIT ID</Text></View>
                <View style={{ ...styles.tableHeaderCell, width: '40%' }}><Text>DESCRIPTION</Text></View>
                <View style={{ ...styles.tableHeaderCell, width: '10%' }}><Text>STATUS</Text></View>
                <View style={{ ...styles.tableHeaderCell, width: '20%' }}><Text>SET</Text></View>
                <View style={{ ...styles.tableHeaderCell, width: '5%' }}><Text style={{ textAlign: 'center' }}>QTY</Text></View>
                <View style={{ ...styles.tableHeaderCell, width: '10%' }}><Text style={{ textAlign: 'center' }}>PICKED</Text></View>
              </View>

              {kits.map((kit) => (
                <View key={kit.id} style={styles.tableRow} wrap={false}>
                  <View style={{ ...styles.tableCell, width: '15%' }}><Text>{kit.kitId}</Text></View>
                  <View style={{ ...styles.tableCell, width: '40%', flexShrink: 1, overflow: 'hidden' }}><Text
                    numberOfLines={1} ellipsizeMode="tail">{kit.name}</Text></View>
                  <View style={{ ...styles.tableCell, width: '10%' }}><Text>{kit.status}</Text></View>
                  <View style={{ ...styles.tableCell, width: '20%' }}><Text>{kit.sets}</Text></View>
                  <View style={{ ...styles.tableCell, width: '5%', textAlign: 'center' }}><Text style={{ textAlign: 'center' }}>{kit.quantity}</Text></View>
                  <View style={{ ...styles.tableCell, alignItems: 'center', width: '10%' }}>
                    <View style={styles.borderCell}>
                      <Text style={{ textAlign: 'center' }}></Text>
                    </View>
                  </View>
                </View>
              ))}
            </View>
          </View>

          {!!items.length && (
            <View style={{ marginTop: 20 }}>
              <Text style={{ fontWeight: 'bold' }}>ITEMS</Text>
              <View style={styles.table}>
                <View style={styles.tableHeader}>
                  <View style={{ ...styles.tableHeaderCell, width: '15%' }}><Text>ITEM CODE</Text></View>
                  <View style={{ ...styles.tableHeaderCell, width: '40%' }}><Text>DESCRIPTION</Text></View>
                  <View style={{ ...styles.tableHeaderCell, width: '10%' }}><Text>STATUS</Text></View>
                  <View style={{ ...styles.tableHeaderCell, width: '20%' }}><Text>SERIAL</Text></View>
                  <View style={{ ...styles.tableHeaderCell, width: '5%' }}><Text style={{ textAlign: 'center' }}>QTY</Text></View>
                  <View style={{ ...styles.tableHeaderCell, width: '10%' }}><Text style={{ textAlign: 'center' }}>PICKED</Text></View>
                </View>

                {items.map((item) => (
                  <View key={item.id} style={styles.tableRow} wrap={false}>
                    <View style={{ ...styles.tableCell, width: '15%' }}><Text>{item.code}</Text></View>
                    <View style={{ ...styles.tableCell, width: '40%', flexShrink: 1, overflow: 'hidden' }}><Text
                      numberOfLines={1} ellipsizeMode="tail">{item.description}</Text></View>
                    <View style={{ ...styles.tableCell, width: '10%' }}><Text>{item.status}</Text></View>
                    <View style={{ ...styles.tableCell, width: '20%' }}><Text>{item.serial}</Text></View>
                    <View style={{ ...styles.tableCell, width: '5%', textAlign: 'center' }}><Text style={{ textAlign: 'center' }}>{item.quantity}</Text></View>
                    <View style={{ ...styles.tableCell, alignItems: 'center', width: '10%' }}>
                      <View style={styles.borderCell}>
                        <Text style={{ textAlign: 'center' }}></Text>
                      </View>
                    </View>
                  </View>
                ))}
              </View>
            </View>
          )}
        </View>

        <View style={styles.footer}>
          <View style={{ display: 'flex', flexDirection: 'row' }}>
            <Text style={styles.bold}>PICK LIST</Text>
            <Text style={{ marginLeft: 16 }}>
              {`${moment()?.local()?.format('Do MMMM YYYY')} | ${user.firstName} ${user.lastName}`}
            </Text>
          </View>
          <View>
            <Text>{activeCase?.id}</Text>
          </View>
        </View>

        <Text
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          fixed
          style={{ position: 'absolute', bottom: 16, left: 36 }}
        />
      </Page>
    </Document>
  );
};

export default PickingListPDF;